import { apiService } from '../../services/api';
import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../contexts/AppContext';
import { BOOK_SIZES, getBookSizeByKey, BookSize } from '@shared/book-size-constants';
import { useBookStructure } from '../../hooks/useBookStructureData';
import { createElement, type CoverElement, type CoverType, type ElementType } from '@shared/cover-elements';

// Model information for display purposes
const MODEL_DISPLAY_NAMES = {
  'imagen-4.0-fast-generate-001': 'Fast',
  'imagen-4.0-generate-001': 'Standard',
  'imagen-4.0-ultra-generate-001': 'Ultra'
};

// Helper function to convert book sizes to image dimensions
const convertBookSizeToImageDimensions = (bookSize: BookSize) => {
  const [ratioW, ratioH] = bookSize.ratio.split(':').map(Number);

  // For KDP sizes, use exact pixel dimensions
  if (bookSize.key === 'kdp-ebook') {
    return { width: 1600, height: 2400 };
  }
  if (bookSize.key === 'kdp-paperback-6x9') {
    return { width: 1838, height: 2775 };
  }
  if (bookSize.key === 'kdp-paperback-5x8') {
    return { width: 1538, height: 2438 };
  }

  // For other sizes, scale to reasonable image dimensions
  // Base dimension 1024, scale proportionally
  const maxDim = 1536;
  const ratio = ratioW / ratioH;

  if (ratio > 1) {
    // Landscape
    return { width: maxDim, height: Math.round(maxDim / ratio) };
  } else {
    // Portrait or square
    return { width: Math.round(maxDim * ratio), height: maxDim };
  }
};

// Generate size presets from book sizes, with KDP first
const generateSizePresets = () => {
  const presets: Record<string, { width: number; height: number; aspectRatio: string; label: string }> = {};

  // Add all book sizes as presets
  BOOK_SIZES.forEach(category => {
    category.sizes.forEach(size => {
      const dimensions = convertBookSizeToImageDimensions(size);
      presets[size.key] = {
        width: dimensions.width,
        height: dimensions.height,
        aspectRatio: size.ratio,
        label: `${size.label} (${dimensions.width}×${dimensions.height})`
      };
    });
  });

  // Add some generic presets at the end
  presets['square-generic'] = { width: 1024, height: 1024, aspectRatio: '1:1', label: 'Square Generic (1024×1024)' };
  presets['landscape-generic'] = { width: 1536, height: 1024, aspectRatio: '3:2', label: 'Landscape Generic (1536×1024)' };
  presets['wide-generic'] = { width: 1792, height: 1024, aspectRatio: '16:9', label: 'Wide Generic (1792×1024)' };

  return presets;
};

const SIZE_PRESETS = generateSizePresets();

const ImageGenerationModal: React.FC = () => {
  const app = useContext(AppContext);
  if (!app) return null;

  const {
    modalStates,
    setProjectState,
  } = app;

  const {
    isImageGenerationModalOpen,
    imageGenerationContext,
    imageGenerationPrompt,
    setImageGenerationPrompt,
    setIsImageGenerationModalOpen,
    generatedImageDataUrl,
    setGeneratedImageDataUrl,
  } = modalStates;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedSizePreset, setSelectedSizePreset] = useState<string>('kdp-ebook'); // Default to KDP ebook
  const [customWidth, setCustomWidth] = useState<string>('');
  const [customHeight, setCustomHeight] = useState<string>('');
  const [useCustomSize, setUseCustomSize] = useState(false);

  // Multi-variant generation state
  const [generatedVariants, setGeneratedVariants] = useState<any[]>([]);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number>(0);
  const [showVariantSelection, setShowVariantSelection] = useState(false);

  // Load book structure data for author details
  const projectState = (app as any).coreState?.projectState || app?.projectState;
  const projectId = projectState?.projectId || '';
  const { data: bookStructureData } = useBookStructure(projectId, !!projectId);

  // Cover element selection state
  const [selectedElements, setSelectedElements] = useState({
    // Front Cover Elements
    title: true, // Always selected
    authorName: true, // Always selected
    subtitle: false,
    seriesInfo: false,
    publisherLogo: false,
    frontCoverEndorsement: false,
    awardsBadge: false,
    // Back Cover Elements
    bookBlurb: true, // Always selected for back covers
    authorBio: true, // Always selected for back covers
    hookLine: false,
    authorPhoto: false,
    backCoverEndorsements: false,
    isbn: false,
    publisherInfo: false,
    socialMedia: false,
    qrCode: false
  });

  // Sync with book format selection from front matter
  useEffect(() => {
    if (isImageGenerationModalOpen && app.projectState?.frontMatter?.titleImageAspectRatio) {
      const aspectRatio = app.projectState.frontMatter.titleImageAspectRatio;

      // Find the book size key that matches this aspect ratio
      const matchingBookSize = BOOK_SIZES
        .flatMap(category => category.sizes)
        .find(size => size.ratio === aspectRatio);

      if (matchingBookSize && SIZE_PRESETS[matchingBookSize.key]) {
        setSelectedSizePreset(matchingBookSize.key);
      }
    }
  }, [isImageGenerationModalOpen, app.projectState?.frontMatter?.titleImageAspectRatio]);

  if (!isImageGenerationModalOpen) return null;

  const close = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsImageGenerationModalOpen(false);
    setErrorMsg(null);
  };

  // Helper function to build structured cover request
  const buildCoverRequest = () => {
    const isBack = imageGenerationContext === 'backCover';
    const { projectState } = app;

    // Extract book metadata from segregated sources
    // Title: Try bookMetadata first, fallback to blueprint, then dbProject
    const bookTitle = projectState?.bookMetadata?.title ||
                     projectState?.blueprint?.worldBuildingSuggestions?.selectedStoryTitle ||
                     app?.dbProject?.title ||
                     'Untitled Book';

    // Try multiple fallback paths for author name (now includes bookStructureData)
    const authorName = projectState?.authorDetails?.authorName ||
                      bookStructureData?.authorDetails?.authorName ||
                      (app?.dbProject as any)?.authorDetails?.authorName ||
                      app?.dbProject?.projectData?.authorDetails?.authorName ||
                      'Unknown Author';

    // Debug logging to troubleshoot author name issue
    console.log('🔍 Author Name Debug:', {
      'projectState.authorDetails': projectState?.authorDetails,
      'bookStructureData.authorDetails': bookStructureData?.authorDetails,
      'app.dbProject.authorDetails': (app?.dbProject as any)?.authorDetails,
      'app.dbProject.projectData.authorDetails': app?.dbProject?.projectData?.authorDetails,
      'finalAuthorName': authorName
    });

    // Build book metadata using segregated data sources
    const bookMetadata = {
      title: bookTitle,
      author: authorName,
      // Use bookMetadata for publishing details
      subtitle: projectState?.bookMetadata?.subtitle || projectState?.frontMatter?.titleImagePrompt || undefined,
      series: projectState?.authorDetails?.seriesDisplayInfo || undefined,
      blurb: projectState?.bookMetadata?.blurb || projectState?.backMatter?.content || undefined,
      isbn: projectState?.authorDetails?.isbn || undefined,
      publisher: projectState?.authorDetails?.publisher || undefined,
      // Use blueprint.worldBuildingSuggestions for creative elements
      genre: projectState?.blueprint?.worldBuildingSuggestions?.themes?.[0] || 'fiction',
      themes: projectState?.blueprint?.worldBuildingSuggestions?.themes || [],
      // Use bookMetadata.overallMood, fallback to default
      overallMood: projectState?.bookMetadata?.overallMood || 'mysterious',
      visualDescription: typeof imageGenerationPrompt === 'string' ? imageGenerationPrompt : ''
    };

    // Get author details from various possible sources (now includes bookStructureData)
    // Check if projectState.authorDetails has meaningful content, not just existence
    const hasProjectStateAuthorDetails = projectState?.authorDetails &&
                                        Object.values(projectState.authorDetails).some(value =>
                                          value && String(value).trim() !== '');

    const authorDetails = hasProjectStateAuthorDetails
                         ? projectState.authorDetails
                         : (bookStructureData?.authorDetails ||
                            (app?.dbProject as any)?.authorDetails ||
                            {});

    // Build selected elements array as proper CoverElement objects
    const selectedElementsArray: CoverElement[] = [];
    const coverType: CoverType = isBack ? 'back' : 'front';

    // Helper function to get content for element types
    const getElementContent = (elementType: ElementType): string => {
      switch (elementType) {
        case 'title':
          return bookMetadata.title;
        case 'author':
          return bookMetadata.author;
        case 'subtitle':
          return bookMetadata.subtitle || '';
        case 'series':
          return ''; // TODO: Get from series info
        case 'blurb':
          return bookMetadata.blurb || '';
        case 'bio':
          return authorDetails.authorBio || '';
        case 'endorsement':
          return isBack ? (authorDetails.backCoverEndorsements || '') : (authorDetails.frontCoverEndorsements || '');
        case 'award':
          return authorDetails.awardsAndBadges?.[0] || '';
        case 'isbn':
          return bookMetadata.isbn || '';
        case 'publisher_info':
          return authorDetails.publisher || '';
        case 'social_media':
          return authorDetails.socialMediaHandles || '';
        case 'qr_code':
          return ''; // TODO: Get from QR code URL
        case 'author_photo':
          return authorDetails.authorProfileImageBase64 || '';
        case 'publisher_logo':
          return ''; // TODO: Get publisher logo
        default:
          return '';
      }
    };

    if (isBack) {
      // Back cover elements
      if (selectedElements.bookBlurb) {
        selectedElementsArray.push(createElement('blurb', getElementContent('blurb'), coverType));
      }
      if (selectedElements.authorBio) {
        selectedElementsArray.push(createElement('bio', getElementContent('bio'), coverType));
      }
      if (selectedElements.hookLine) {
        selectedElementsArray.push(createElement('tagline', authorDetails.backCoverHookLine || '', coverType));
      }
      if (selectedElements.authorPhoto) {
        selectedElementsArray.push(createElement('author_photo', getElementContent('author_photo'), coverType));
      }
      if (selectedElements.backCoverEndorsements) {
        selectedElementsArray.push(createElement('endorsement', getElementContent('endorsement'), coverType));
      }
      if (selectedElements.isbn) {
        selectedElementsArray.push(createElement('isbn', getElementContent('isbn'), coverType));
      }
      if (selectedElements.publisherInfo) {
        selectedElementsArray.push(createElement('publisher_info', getElementContent('publisher_info'), coverType));
      }
      if (selectedElements.socialMedia) {
        selectedElementsArray.push(createElement('social_media', getElementContent('social_media'), coverType));
      }
      if (selectedElements.qrCode) {
        selectedElementsArray.push(createElement('qr_code', getElementContent('qr_code'), coverType));
      }
    } else {
      // Front cover elements
      if (selectedElements.title) {
        selectedElementsArray.push(createElement('title', getElementContent('title'), coverType));
      }
      if (selectedElements.authorName) {
        selectedElementsArray.push(createElement('author', getElementContent('author'), coverType));
      }
      if (selectedElements.subtitle) {
        selectedElementsArray.push(createElement('subtitle', getElementContent('subtitle'), coverType));
      }
      if (selectedElements.seriesInfo) {
        selectedElementsArray.push(createElement('series', getElementContent('series'), coverType));
      }
      if (selectedElements.publisherLogo) {
        selectedElementsArray.push(createElement('publisher_logo', getElementContent('publisher_logo'), coverType));
      }
      if (selectedElements.frontCoverEndorsement) {
        selectedElementsArray.push(createElement('endorsement', getElementContent('endorsement'), coverType));
      }
      if (selectedElements.awardsBadge) {
        selectedElementsArray.push(createElement('award', getElementContent('award'), coverType));
      }
    }

    // Build print specifications (server expects trimSize in inches, not pixels)
    let printSpecs;
    if (useCustomSize) {
      const width = parseInt(customWidth);
      const height = parseInt(customHeight);
      // Convert pixels to inches at 300 DPI
      const trimWidthInches = width / 300;
      const trimHeightInches = height / 300;

      printSpecs = {
        trimSize: { width: trimWidthInches, height: trimHeightInches },
        bleedSize: 0.125, // Standard 0.125" bleed
        safeArea: 0.25,   // Standard 0.25" safe area
        dpi: 300
      };
    } else {
      const preset = SIZE_PRESETS[selectedSizePreset];
      if (preset) {
        // Convert pixels to inches at 300 DPI
        const trimWidthInches = preset.width / 300;
        const trimHeightInches = preset.height / 300;

        printSpecs = {
          trimSize: { width: trimWidthInches, height: trimHeightInches },
          bleedSize: 0.125, // Standard 0.125" bleed
          safeArea: 0.25,   // Standard 0.25" safe area
          dpi: 300
        };
      }
    }

    // Build generation config - always use 3 models for variants
    const generationConfig = {
      models: [
        'imagen-4.0-fast-generate-001',    // Fast variant
        'imagen-4.0-generate-001',         // Standard variant
        'imagen-4.0-ultra-generate-001'    // Ultra variant
      ],
      artStyle: 'photorealistic',
      layoutPreference: 'professional',
      backgroundOnly: true // Use background-only strategy for clean text overlay
    };

    console.log('🔍 Author Details Resolution:', {
      hasProjectStateAuthorDetails,
      usingBookStructureData: !hasProjectStateAuthorDetails && !!bookStructureData?.authorDetails,
      finalAuthorDetails: authorDetails,
      authorDetailsSources: {
        projectState: !!projectState?.authorDetails,
        bookStructure: !!bookStructureData?.authorDetails,
        dbProject: !!(app?.dbProject as any)?.authorDetails
      }
    });

    return {
      coverType: isBack ? 'back' : 'front',
      bookMetadata,
      authorDetails,
      selectedElements: {
        [isBack ? 'back' : 'front']: selectedElementsArray
      },
      printSpecs,
      generationConfig
    };
  };

  const handleGenerate = async () => {
    const basePrompt = typeof imageGenerationPrompt === 'string' ? imageGenerationPrompt.trim() : '';
    if (!basePrompt) {
      setErrorMsg('Please enter an image description first.');
      return;
    }

    if (useCustomSize) {
      const width = parseInt(customWidth);
      const height = parseInt(customHeight);
      if (isNaN(width) || isNaN(height) || width < 256 || height < 256) {
        setErrorMsg('Please enter valid dimensions (minimum 256x256).');
        return;
      }
    }

    setIsLoading(true);
    setErrorMsg(null);
    setGeneratedVariants([]);
    setShowVariantSelection(false);

    try {
      // Build structured cover request
      const coverRequest = buildCoverRequest();

      console.log('🎨 Generating cover with advanced API:', {
        coverType: coverRequest.coverType,
        bookTitle: coverRequest.bookMetadata.title,
        author: coverRequest.bookMetadata.author,
        models: coverRequest.generationConfig.models.length,
        elements: coverRequest.selectedElements[coverRequest.coverType]?.length || 0
      });

      const json = await apiService.post('/api/llm/covers/generate', { coverRequest });

      if (!json.success) {
        throw new Error(json.message || 'Cover generation failed');
      }

      const response = json.data;
      const variants = response?.variants || [];

      if (variants.length === 0) {
        throw new Error('No cover variants generated');
      }

      // Store all variants
      setGeneratedVariants(variants);
      setSelectedVariantIndex(0);

      // Set the first variant as the current image for backward compatibility
      const firstVariant = variants[0];
      const dataUrl = `data:image/jpeg;base64,${firstVariant.image}`;
      setGeneratedImageDataUrl(dataUrl);

      // Show variant selection if we have multiple variants
      if (variants.length > 1) {
        setShowVariantSelection(true);
        setErrorMsg(`✅ Generated ${variants.length} cover variants! Select your preferred version below.`);
      } else {
        setErrorMsg(`✅ Cover generated successfully with ${firstVariant.metadata?.model || 'advanced model'}!`);
      }

    } catch (e: any) {
      console.error('Cover generation error:', e);
      setErrorMsg(e.message || 'Cover generation failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseImage = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!generatedImageDataUrl) return;
    // Accept both legacy ('titlePageImage') and new ('titlePage') keys
    const isTitle = imageGenerationContext === 'titlePage' || imageGenerationContext === 'coverArt';
    const isBack = imageGenerationContext === 'backCover';

    setProjectState(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      if (isTitle) {
        next.frontMatter = {
          ...next.frontMatter,
          titleImageBase64: generatedImageDataUrl,
        };
      } else if (isBack) {
        next.backMatter = {
          ...next.backMatter,
          backCoverImageGeneratedBase64: generatedImageDataUrl,
        };
      }
      next.isUnsaved = true;
      return next;
    });

    close();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      close(e);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">
            {imageGenerationContext === 'backCover' ? 'Generate Back Cover Image' : 'Generate Title Image'}
          </h3>
          <button
            onClick={(e) => close(e)}
            className="text-sm px-2 py-1 rounded border hover:bg-gray-100"
            type="button"
          >
            Close
          </button>
        </div>

        <label className="block text-sm font-medium mb-1">Image description</label>
        <textarea
          value={typeof imageGenerationPrompt === 'string' ? imageGenerationPrompt : ''}
          onChange={(e) => setImageGenerationPrompt(e.target.value)}
          className="w-full border rounded p-2 min-h-[90px] resize-none"
          placeholder="Describe the image you want..."
        />

        {/* Advanced Generation Info */}
        <div className="mt-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
            <div className="font-medium text-blue-800 mb-1">✨ Professional Cover Generation</div>
            <div className="text-blue-700">
              This will generate 3 cover variants using Fast, Standard, and Ultra quality models.
              You can compare and choose the best result for your book.
            </div>
          </div>
        </div>

        {/* Size Configuration */}
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1">Image Size</label>

          <div className="flex items-center gap-2 mb-2">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={!useCustomSize}
                onChange={() => setUseCustomSize(false)}
                className="text-blue-600"
              />
              <span className="text-sm">Preset sizes</span>
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={useCustomSize}
                onChange={() => setUseCustomSize(true)}
                className="text-blue-600"
              />
              <span className="text-sm">Custom size</span>
            </label>
          </div>

          {!useCustomSize ? (
            <>
              {/* Show current book format selection */}
              {app.projectState?.frontMatter?.titleImageAspectRatio && (
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                  <span className="font-medium text-blue-800">📖 Current Book Format: </span>
                  <span className="text-blue-700">
                    {(() => {
                      const aspectRatio = app.projectState.frontMatter.titleImageAspectRatio;
                      const matchingSize = BOOK_SIZES
                        .flatMap(category => category.sizes)
                        .find(size => size.ratio === aspectRatio);
                      return matchingSize?.label || `Aspect Ratio: ${aspectRatio}`;
                    })()}
                  </span>
                </div>
              )}

              <select
                value={selectedSizePreset}
                onChange={(e) => setSelectedSizePreset(e.target.value)}
                className="w-full border rounded p-2 text-sm"
              >
                {BOOK_SIZES.map(category => (
                  <optgroup key={category.category} label={category.label}>
                    {category.sizes.map(size => {
                      const preset = SIZE_PRESETS[size.key];
                      if (!preset) return null;
                      const isCurrentFormat = app.projectState?.frontMatter?.titleImageAspectRatio === size.ratio;
                      return (
                        <option key={size.key} value={size.key}>
                          {isCurrentFormat ? '✓ ' : ''}{preset.label} • {preset.aspectRatio}
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
                <optgroup label="Generic Sizes">
                  <option value="square-generic">{SIZE_PRESETS['square-generic'].label}</option>
                  <option value="landscape-generic">{SIZE_PRESETS['landscape-generic'].label}</option>
                  <option value="wide-generic">{SIZE_PRESETS['wide-generic'].label}</option>
                </optgroup>
              </select>
            </>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="number"
                  placeholder="Width"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(e.target.value)}
                  min="256"
                  max="2048"
                  className="w-full border rounded p-2 text-sm"
                />
              </div>
              <div className="flex items-center text-gray-500">×</div>
              <div className="flex-1">
                <input
                  type="number"
                  placeholder="Height"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(e.target.value)}
                  min="256"
                  max="2048"
                  className="w-full border rounded p-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="text-xs text-gray-600 mt-1">
            {!useCustomSize && SIZE_PRESETS[selectedSizePreset] &&
              `${SIZE_PRESETS[selectedSizePreset].width}×${SIZE_PRESETS[selectedSizePreset].height} pixels`
            }
            {useCustomSize && customWidth && customHeight &&
              `${customWidth}×${customHeight} pixels`
            }
          </div>
        </div>

        {/* Cover Element Selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2">
            {imageGenerationContext === 'backCover' ? 'Back Cover Elements' : 'Front Cover Elements'}
          </label>
          <div className="grid grid-cols-2 gap-2 p-3 border rounded bg-gray-50">
            {imageGenerationContext === 'backCover' ? (
              // Back Cover Elements
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.bookBlurb}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, bookBlurb: e.target.checked }))}
                    className="rounded"
                  />
                  ✅ Book Blurb (Required)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.authorBio}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, authorBio: e.target.checked }))}
                    className="rounded"
                  />
                  ✅ Author Bio (Required)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.hookLine}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, hookLine: e.target.checked }))}
                    className="rounded"
                  />
                  📢 Hook Line
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.authorPhoto}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, authorPhoto: e.target.checked }))}
                    className="rounded"
                  />
                  📷 Author Photo
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.backCoverEndorsements}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, backCoverEndorsements: e.target.checked }))}
                    className="rounded"
                  />
                  💬 Endorsements
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.isbn}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, isbn: e.target.checked }))}
                    className="rounded"
                  />
                  📚 ISBN/Barcode
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.publisherInfo}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, publisherInfo: e.target.checked }))}
                    className="rounded"
                  />
                  🏢 Publisher Info
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.socialMedia}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, socialMedia: e.target.checked }))}
                    className="rounded"
                  />
                  📱 Social Media
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.qrCode}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, qrCode: e.target.checked }))}
                    className="rounded"
                  />
                  🔗 QR Code
                </label>
              </>
            ) : (
              // Front Cover Elements
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.title}
                    disabled
                    className="rounded"
                  />
                  ✅ Title (Required)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.authorName}
                    disabled
                    className="rounded"
                  />
                  ✅ Author Name (Required)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.subtitle}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, subtitle: e.target.checked }))}
                    className="rounded"
                  />
                  📖 Subtitle/Tagline
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.seriesInfo}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, seriesInfo: e.target.checked }))}
                    className="rounded"
                  />
                  📚 Series Info
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.publisherLogo}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, publisherLogo: e.target.checked }))}
                    className="rounded"
                  />
                  🏢 Publisher Logo
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.frontCoverEndorsement}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, frontCoverEndorsement: e.target.checked }))}
                    className="rounded"
                  />
                  💬 Front Endorsement
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedElements.awardsBadge}
                    onChange={(e) => setSelectedElements(prev => ({ ...prev, awardsBadge: e.target.checked }))}
                    className="rounded"
                  />
                  🏆 Awards Badge
                </label>
              </>
            )}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            Select which elements to include in the generated cover. Only elements with available data will affect the image.
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={handleGenerate}
            disabled={isLoading}
            className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-60 hover:bg-blue-700 disabled:hover:bg-blue-600"
          >
            {isLoading ? 'Generating...' : 'Generate'}
          </button>
          {errorMsg && <span className={`text-sm ${errorMsg.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>{errorMsg}</span>}
        </div>

        {generatedImageDataUrl && (
          <>
            <div className="mt-4">
              <img
                src={generatedImageDataUrl}
                alt="Generated"
                className="max-h-[320px] w-auto rounded border"
              />
            </div>

            {/* Variant Selection UI */}
            {showVariantSelection && generatedVariants.length > 1 && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Choose Your Preferred Variant ({generatedVariants.length} options)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {generatedVariants.map((variant, index) => {
                    const isSelected = index === selectedVariantIndex;
                    const modelKey = variant.metadata?.model || '';
                    const modelName = MODEL_DISPLAY_NAMES[modelKey as keyof typeof MODEL_DISPLAY_NAMES] || `Model ${index + 1}`;
                    const cost = variant.metadata?.estimatedCost || 'Unknown';

                    return (
                      <div
                        key={index}
                        className={`relative border-2 rounded-lg p-2 cursor-pointer transition-all ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setSelectedVariantIndex(index);
                          const dataUrl = `data:image/jpeg;base64,${variant.image}`;
                          setGeneratedImageDataUrl(dataUrl);
                        }}
                      >
                        <img
                          src={`data:image/jpeg;base64,${variant.image}`}
                          alt={`Variant ${index + 1}`}
                          className="w-full h-24 object-cover rounded"
                        />
                        <div className="mt-1 text-xs text-center">
                          <div className="font-medium capitalize">{modelName}</div>
                          <div className="text-gray-600">{cost} credits</div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  Click on a variant to select it. Each variant was generated with a different AI model for quality comparison.
                </div>
              </div>
            )}

            <div className="mt-3">
              <button
                onClick={(e) => handleUseImage(e)}
                className="px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700"
                type="button"
              >
                Use this image
              </button>
              {showVariantSelection && (
                <span className="ml-2 text-sm text-gray-600">
                  ({MODEL_DISPLAY_NAMES[generatedVariants[selectedVariantIndex]?.metadata?.model as keyof typeof MODEL_DISPLAY_NAMES] || 'Selected'} variant)
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImageGenerationModal;